import * as pc from 'playcanvas';
import { matCache } from '../utils/MaterialCache';

// RGB triples matching server colorIndex values (0=blue, 1=orange, 2=teal)
const PORTAL_COLORS: [number, number, number][] = [
  [0.2, 0.5, 1.0],
  [1.0, 0.5, 0.1],
  [0.1, 0.9, 0.6],
];

export class PortalEntity {
  entity: pc.Entity;
  private ring: pc.Entity;
  private pillar: pc.Entity;
  private ringMat: pc.StandardMaterial;
  private pillarMat: pc.StandardMaterial;
  private time: number = 0;
  private ringRotY: number = 0;

  constructor(app: pc.Application, id: string, x: number, z: number, colorIndex: number) {
    const [r, g, b] = PORTAL_COLORS[colorIndex % PORTAL_COLORS.length];

    this.entity = new pc.Entity(`portal_${id}`);
    app.root.addChild(this.entity);
    this.entity.setPosition(x, 0, z);

    // Outer glow ring (wide flat cylinder)
    this.ring = new pc.Entity('ring');
    this.ring.addComponent('render', { type: 'cylinder' });
    this.ringMat = matCache.createUnique({
      diffuse: new pc.Color(r * 0.3, g * 0.3, b * 0.3),
      emissive: new pc.Color(r, g, b),
      emissiveIntensity: 1.8,
    });
    this.ring.render!.meshInstances[0].material = this.ringMat;
    this.ring.render!.castShadows = false;
    this.ring.render!.receiveShadows = false;
    this.ring.setLocalScale(5, 0.08, 5);
    this.ring.setLocalPosition(0, 0.04, 0);
    this.entity.addChild(this.ring);

    // Inner dark disc — sits on top of the ring to create a hollow ring appearance
    const inner = new pc.Entity('innerDark');
    inner.addComponent('render', { type: 'cylinder' });
    inner.render!.meshInstances[0].material = matCache.get({
      diffuse: new pc.Color(0.02, 0.02, 0.03),
      emissive: new pc.Color(0, 0, 0),
    });
    inner.render!.castShadows = false;
    inner.render!.receiveShadows = false;
    inner.setLocalScale(3.4, 0.1, 3.4);
    inner.setLocalPosition(0, 0.06, 0);
    this.entity.addChild(inner);

    // Vertical glow pillar — makes the portal visible from a distance
    this.pillar = new pc.Entity('pillar');
    this.pillar.addComponent('render', { type: 'cylinder' });
    this.pillarMat = matCache.createUnique({
      diffuse: new pc.Color(r * 0.2, g * 0.2, b * 0.2),
      emissive: new pc.Color(r, g, b),
      emissiveIntensity: 1.2,
    });
    this.pillar.render!.meshInstances[0].material = this.pillarMat;
    this.pillar.render!.castShadows = false;
    this.pillar.render!.receiveShadows = false;
    this.pillar.setLocalScale(0.35, 3.5, 0.35);
    this.pillar.setLocalPosition(0, 1.75, 0);
    this.entity.addChild(this.pillar);
  }

  update(dt: number) {
    this.time += dt;

    // Slowly spin the ring
    this.ringRotY += 40 * dt;
    this.ring.setLocalEulerAngles(0, this.ringRotY, 0);

    // Pulse the pillar's emissive intensity and bob it slightly
    this.pillarMat.emissiveIntensity = 1.0 + Math.sin(this.time * 2.2) * 0.5;
    this.pillarMat.update();
    this.pillar.setLocalPosition(0, 1.75 + Math.sin(this.time * 1.5) * 0.08, 0);
  }

  destroy() {
    this.entity.destroy();
  }
}
